import React, { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig';
import './AddEventPage.css'; // reuse shared form styles

const DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Calicut', 'Wayanad', 'Kannur', 'Kasaragod',
];

const INITIAL = {
  title:     '',
  body:      '',
  imageUrl:  '',
  target:    'all',     // 'all' or a district name
};

export default function SendNotificationPage() {
  const [form,       setForm]       = useState(INITIAL);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required.';
    if (!form.body.trim())  e.body  = 'Message body is required.';
    if (form.title.trim().length > 65) e.title = 'Keep the title under 65 characters.';
    if (form.body.trim().length > 240) e.body  = 'Keep the body under 240 characters.';
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    if (!window.confirm(
      `Send this notification to ${form.target === 'all'
        ? 'ALL members'
        : `members in ${form.target}`}?`
    )) {
      return;
    }

    setSubmitting(true);
    setToast(null);

    try {
      const call = httpsCallable(functions, 'sendNotification');
      await call({
        title:    form.title.trim(),
        body:     form.body.trim(),
        imageUrl: form.imageUrl.trim() || undefined,
        target:   form.target,
      });

      setToast({
        type: 'success',
        message: `✅ Notification sent to ${
          form.target === 'all' ? 'all members' : form.target
        }.`,
      });
      setForm(INITIAL);
    } catch (err) {
      setToast({
        type: 'error',
        message: `❌ Failed: ${err.message || err.code || 'Unknown error'}`,
      });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  const titleChars = form.title.length;
  const bodyChars  = form.body.length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Send Notification</h1>
          <p className="page-subtitle">
            Push a message to members&apos; phones. Delivered instantly via FCM.
          </p>
        </div>
      </div>

      {toast && (
        <div
          className={`amf-toast amf-toast--${toast.type}`}
          style={{ marginBottom: 24 }}
        >
          {toast.message}
          <button
            className="amf-toast-close"
            onClick={() => setToast(null)}
          >
            ×
          </button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div className="aep-grid">

          {/* Left column */}
          <div className="aep-col">
            <Field
              label={`Title (${titleChars}/65)`}
              required
              error={errors.title}
            >
              <input
                name="title"
                className={`amf-input ${errors.title ? 'amf-input--error' : ''}`}
                placeholder="e.g. Monsoon Ride this Sunday!"
                maxLength={70}
                value={form.title}
                onChange={handleChange}
              />
            </Field>

            <Field
              label={`Message Body (${bodyChars}/240)`}
              required
              error={errors.body}
            >
              <textarea
                name="body"
                className={`amf-input aep-textarea ${errors.body ? 'amf-input--error' : ''}`}
                placeholder="Details members will see in the notification…"
                rows={5}
                maxLength={250}
                value={form.body}
                onChange={handleChange}
              />
              <p className="amf-hint">
                Short, action-oriented messages get better open rates.
              </p>
            </Field>

            <Field label="Image URL (optional)">
              <input
                name="imageUrl"
                type="url"
                className="amf-input"
                placeholder="https://firebasestorage.googleapis.com/…"
                value={form.imageUrl}
                onChange={handleChange}
              />
              <p className="amf-hint">
                Shown as a big-picture notification on Android. Optional.
              </p>
            </Field>
          </div>

          {/* Right column */}
          <div className="aep-col">
            <Field label="Send To" required>
              <select
                name="target"
                className="amf-select"
                value={form.target}
                onChange={handleChange}
              >
                <option value="all">🌐 All Members</option>
                <optgroup label="By District">
                  {DISTRICTS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </optgroup>
              </select>
              <p className="amf-hint">
                {form.target === 'all'
                  ? 'Every member who has the app installed will get this.'
                  : `Only members in ${form.target} will get this.`}
              </p>
            </Field>

            {/* Live preview */}
            <Field label="Preview">
              <div
                style={{
                  background: '#161618',
                  border: '1px solid #1FD4B574',
                  borderRadius: 12,
                  padding: 14,
                  color: '#F7E8BF',
                }}
              >
                <div style={{
                  fontSize: 11,
                  color: '#B79F6D',
                  letterSpacing: 1,
                  marginBottom: 8,
                  textTransform: 'uppercase',
                }}>
                  Team Fronx · now
                </div>
                <div style={{
                  fontSize: 15,
                  fontWeight: 700,
                  marginBottom: 4,
                  color: '#F7E8BF',
                }}>
                  {form.title.trim() || 'Notification title appears here'}
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#D8C8A4',
                  lineHeight: 1.45,
                }}>
                  {form.body.trim() || 'The message body will appear here…'}
                </div>
                {form.imageUrl.trim() && (
                  <img
                    src={form.imageUrl.trim()}
                    alt=""
                    style={{
                      marginTop: 10,
                      width: '100%',
                      borderRadius: 8,
                      maxHeight: 160,
                      objectFit: 'cover',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
            </Field>
          </div>
        </div>

        <div className="aep-submit-row">
          <button
            type="submit"
            className="amf-submit-btn"
            disabled={submitting}
          >
            {submitting
              ? <><span className="amf-spinner" /> Sending…</>
              : 'Send Notification'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}
